'use client';

import React, { useState, useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import { useTranslation } from '@/hooks/useTranslation';
import { useSupabaseAuth } from '@/hooks/useSupabaseAuth';
import { createClient } from '@/lib/supabase/client';

interface CarouselSlide {
    image: string;
    direction: 'left' | 'center' | 'right';
    eraKey: string;
    descKey: string;
}

interface SearchResult {
    id: string;
    type: 'manufacturer' | 'product' | 'category';
    name: string;
    description?: string;
    image?: string;
    link: string;
}

const HeroCarousel = () => {
    const { t } = useTranslation();
    const { user, loading } = useSupabaseAuth();
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
    const [showResults, setShowResults] = useState(false);
    const [searching, setSearching] = useState(false);
    const searchRef = useRef<HTMLDivElement>(null);

    const carouselSlides: CarouselSlide[] = [
        {
            image: '/images/heritage/pharaonic-weaving.jpg',
            direction: 'left',
            eraKey: 'ancientEgypt',
            descKey: 'ancientEgyptDesc',
        },
        {
            image: '/images/heritage/traditional-craftsman.jpg',
            direction: 'center',
            eraKey: 'traditionalCraftsmanship',
            descKey: 'traditionalCraftsmanshipDesc',
        },
        {
            image: '/images/heritage/modern-factory.jpg',
            direction: 'right',
            eraKey: 'modernExcellence',
            descKey: 'modernExcellenceDesc',
        },
    ];

    // Auto-rotate every 6 seconds
    useEffect(() => {
        if (isPaused) return;

        const interval = setInterval(() => {
            setCurrentIndex((prevIndex) => (prevIndex + 1) % carouselSlides.length);
        }, 6000);

        return () => clearInterval(interval);
    }, [isPaused, carouselSlides.length]);

    // Click outside to close search results
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
                setShowResults(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Search functionality with debounce
    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (searchQuery.trim().length >= 2) {
                performSearch();
            } else {
                setSearchResults([]);
                setShowResults(false);
            }
        }, 300);

        return () => clearTimeout(delayDebounceFn);
    }, [searchQuery]);

    const performSearch = async () => {
        setSearching(true);
        const supabase = createClient();
        const results: SearchResult[] = [];

        try {
            const searchTerm = searchQuery.trim().toLowerCase();

            // Search Manufacturers
            const { data: manufacturers } = await supabase
                .from('manufacturers')
                .select('id, company_name, description, logo_url')
                .ilike('company_name', `%${searchTerm}%`)
                .eq('is_active', true)
                .limit(5);

            if (manufacturers) {
                manufacturers.forEach(mfr => {
                    results.push({
                        id: mfr.id,
                        type: 'manufacturer',
                        name: mfr.company_name,
                        description: mfr.description,
                        image: mfr.logo_url,
                        link: `/manufacturer/${mfr.id}`
                    });
                });
            }

            // Search Products
            const { data: products } = await supabase
                .from('products')
                .select('id, name, description, images, category')
                .or(`name.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%`)
                .limit(5);

            if (products) {
                products.forEach(prod => {
                    results.push({
                        id: prod.id,
                        type: 'product',
                        name: prod.name,
                        description: prod.category,
                        image: prod.images?.[0] || null,
                        link: `/product-catalog/${prod.id}`
                    });
                });
            }

            // Search Categories (predefined categories)
            const categories = [
                { name: 'Cotton', link: '/product-catalog?category=Cotton' },
                { name: 'Textiles', link: '/product-catalog?category=Textiles' },
                { name: 'Apparel', link: '/product-catalog?category=Apparel' },
                { name: 'Home Textiles', link: '/product-catalog?category=Home Textiles' },
                { name: 'Industrial Textiles', link: '/product-catalog?category=Industrial Textiles' },
                { name: 'Yarn', link: '/product-catalog?category=Yarn' },
                { name: 'Fabric', link: '/product-catalog?category=Fabric' },
                { name: 'Carpets', link: '/product-catalog?category=Carpets' },
                { name: 'Ready-Made Garments', link: '/product-catalog?category=Ready-Made Garments' }
            ];

            categories.forEach(cat => {
                if (cat.name.toLowerCase().includes(searchTerm)) {
                    results.push({
                        id: cat.name,
                        type: 'category',
                        name: cat.name,
                        link: cat.link
                    });
                }
            });

            setSearchResults(results);
            setShowResults(results.length > 0);
        } catch (error) {
            console.error('Search error:', error);
            setSearchResults([]);
        } finally {
            setSearching(false);
        }
    };

    const goToSlide = (index: number) => {
        setCurrentIndex(index);
    };

    const handleSearch = () => {
        if (searchQuery.trim()) {
            performSearch();
            setShowResults(true);
        }
    };

    const handleResultClick = (link: string) => {
        setShowResults(false);
        setSearchQuery('');
        window.location.href = link;
    };

    const getTypeIcon = (type: string) => {
        switch (type) {
            case 'manufacturer':
                return 'BuildingStorefrontIcon';
            case 'product':
                return 'CubeIcon';
            case 'category':
                return 'TagIcon';
            default:
                return 'MagnifyingGlassIcon';
        }
    };

    const getTypeBadgeColor = (type: string) => {
        switch (type) {
            case 'manufacturer':
                return 'bg-blue-500/10 text-blue-400 border-blue-400/30';
            case 'product':
                return 'bg-amber-500/10 text-amber-400 border-amber-400/30';
            case 'category':
                return 'bg-green-500/10 text-green-400 border-green-400/30';
            default:
                return 'bg-gray-500/10 text-gray-400 border-gray-400/30';
        }
    };

    const getSlideAnimation = (index: number, direction: 'left' | 'center' | 'right') => {
        const isActive = index === currentIndex;

        if (!isActive) return 'opacity-0 scale-95';

        // Directional entrance animations
        if (direction === 'left') {
            return 'opacity-100 scale-100 animate-slide-in-left';
        } else if (direction === 'right') {
            return 'opacity-100 scale-100 animate-slide-in-right';
        } else {
            return 'opacity-100 scale-100 animate-fade-in';
        }
    };

    return (
        <section
            className="relative min-h-[92vh] flex items-center justify-center overflow-hidden bg-stone-900 mt-16"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
        >
            {/* Background Images with Directional Animations */}
            {carouselSlides.map((slide, index) => (
                <div
                    key={index}
                    className={`absolute inset-0 transition-all duration-1000 pointer-events-none ${getSlideAnimation(index, slide.direction)}`}
                >
                    <div
                        className="w-full h-full bg-cover bg-center"
                        style={{
                            backgroundImage: `url('${slide.image}')`,
                            filter: 'brightness(0.4)',
                        }}
                    />
                    {/* Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70" />
                </div>
            ))}

            {/* Main Content */}
            <div className="relative z-[5] max-w-6xl mx-auto px-6 text-center pt-20 pointer-events-auto">
                {/* Era Badge */}
                <div className="mb-6 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
                    <span className="inline-block px-6 py-2 bg-amber-600/20 backdrop-blur-sm border border-amber-500/40 text-amber-200 rounded-full text-sm font-semibold tracking-widest uppercase">
                        {t(carouselSlides[currentIndex].eraKey as any)}
                    </span>
                </div>

                {/* Main Headline */}
                <h1
                    className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-4 leading-tight animate-fade-in-up font-serif"
                    style={{ animationDelay: '0.4s', textShadow: '2px 4px 16px rgba(0,0,0,0.8)' }}
                >
                    {t('heroTitle')}
                    <br />
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 via-amber-300 to-amber-400">
                        {t('heroTitleHighlight')}
                    </span>
                </h1>

                {/* Subheadline */}
                <p
                    className="text-xl md:text-2xl text-amber-100 mb-8 max-w-4xl mx-auto leading-relaxed animate-fade-in-up font-medium"
                    style={{ animationDelay: '0.6s', textShadow: '1px 2px 10px rgba(0,0,0,0.8)' }}
                >
                    {t('heroSubtitle')}
                    <br />
                    <span className="text-lg text-amber-200">{t('heroSubtitle2')}</span>
                </p>

                {/* Slide Description */}
                <p
                    className="text-base text-gray-300 mb-10 italic"
                    style={{ textShadow: '1px 2px 8px rgba(0,0,0,0.8)' }}
                >
                    {t(carouselSlides[currentIndex].descKey as any)}
                </p>

                {/* Search Bar */}
                <div className="max-w-3xl mx-auto mb-8" ref={searchRef}>
                    <div className="flex gap-3">
                        <div className="flex-1 relative">
                            <Icon
                                name="MagnifyingGlassIcon"
                                size={22}
                                variant="outline"
                                className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400 z-10"
                            />
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                onFocus={() => searchResults.length > 0 && setShowResults(true)}
                                placeholder={t('searchPlaceholder')}
                                className="w-full pl-14 pr-6 py-5 rounded-xl bg-white/95 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-amber-500 shadow-2xl text-lg"
                            />
                            {searching && (
                                <div className="absolute right-5 top-1/2 -translate-y-1/2">
                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-amber-600"></div>
                                </div>
                            )}

                            {/* Search Results Dropdown */}
                            {showResults && searchResults.length > 0 && (
                                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border border-gray-200 max-h-96 overflow-y-auto z-50">
                                    <div className="p-2">
                                        {searchResults.map((result) => (
                                            <button
                                                key={`${result.type}-${result.id}`}
                                                onClick={() => handleResultClick(result.link)}
                                                className="w-full flex items-center gap-4 p-3 rounded-lg hover:bg-gray-50 transition-colors text-left group"
                                            >
                                                {result.image ? (
                                                    <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                                                        <AppImage
                                                            src={result.image}
                                                            alt={result.name}
                                                            className="w-full h-full object-cover"
                                                        />
                                                    </div>
                                                ) : (
                                                    <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                                                        <Icon
                                                            name={getTypeIcon(result.type)}
                                                            size={24}
                                                            variant="outline"
                                                            className="text-gray-400"
                                                        />
                                                    </div>
                                                )}
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <h4 className="text-sm font-semibold text-gray-900 truncate group-hover:text-amber-600">
                                                            {result.name}
                                                        </h4>
                                                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${getTypeBadgeColor(result.type)}`}>
                                                            {result.type}
                                                        </span>
                                                    </div>
                                                    {result.description && (
                                                        <p className="text-xs text-gray-500 truncate">{result.description}</p>
                                                    )}
                                                </div>
                                                <Icon
                                                    name="ChevronRightIcon"
                                                    size={20}
                                                    variant="outline"
                                                    className="text-gray-400 group-hover:text-amber-600"
                                                />
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* No Results Message */}
                            {showResults && searchResults.length === 0 && !searching && searchQuery.trim().length >= 2 && (
                                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border border-gray-200 p-6 z-50">
                                    <div className="text-center">
                                        <Icon name="MagnifyingGlassIcon" size={48} variant="outline" className="text-gray-300 mx-auto mb-3" />
                                        <p className="text-gray-600 font-medium">No results found</p>
                                        <p className="text-sm text-gray-500 mt-1">Try searching with different keywords</p>
                                    </div>
                                </div>
                            )}
                        </div>
                        <button
                            onClick={handleSearch}
                            className="px-10 py-5 bg-gradient-to-r from-amber-600 to-amber-500 text-white rounded-xl font-bold text-lg hover:from-amber-500 hover:to-amber-400 transition-all duration-300 shadow-2xl hover:shadow-amber-500/50"
                        >
                            {t('searchButton')}
                        </button>
                    </div>
                </div>

                {/* Action Buttons */}
                <div
                    className="flex flex-col sm:flex-row gap-5 justify-center items-center mb-12 animate-fade-in-up"
                    style={{ animationDelay: '0.8s' }}
                >
                    <a
                        href="/manufacturer-directory"
                        className="group flex items-center gap-3 px-10 py-5 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-xl font-bold text-lg hover:from-blue-500 hover:to-blue-400 transition-all duration-300 shadow-2xl hover:shadow-blue-600/50 hover:scale-105 min-w-[260px] justify-center"
                    >
                        <Icon name="BuildingStorefrontIcon" size={24} variant="solid" />
                        <span>{t('browseManufacturersAction')}</span>
                    </a>

                    <a
                        href="/product-catalog"
                        className="group flex items-center gap-3 px-10 py-5 bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 rounded-xl font-bold text-lg hover:bg-white/20 hover:border-white/50 transition-all duration-300 shadow-xl min-w-[260px] justify-center"
                    >
                        <Icon name="CubeIcon" size={24} variant="solid" />
                        <span>{t('browseProductsAction')}</span>
                    </a>

                    {/* Conditional My Account / Sign In Button */}
                    {!loading && (
                        user ? (
                            <a
                                href="/my-account"
                                className="group flex items-center gap-3 px-10 py-5 bg-gradient-to-r from-orange-600 to-orange-500 text-white rounded-xl font-bold text-lg hover:from-orange-500 hover:to-orange-400 transition-all duration-300 shadow-2xl hover:shadow-orange-600/50 hover:scale-105 min-w-[260px] justify-center"
                            >
                                <Icon name="UserCircleIcon" size={24} variant="solid" />
                                <span>My Account</span>
                            </a>
                        ) : (
                            <a
                                href="/signin"
                                className="group flex items-center gap-3 px-10 py-5 bg-gradient-to-r from-orange-600 to-orange-500 text-white rounded-xl font-bold text-lg hover:from-orange-500 hover:to-orange-400 transition-all duration-300 shadow-2xl hover:shadow-orange-600/50 hover:scale-105 min-w-[260px] justify-center"
                            >
                                <Icon name="ArrowRightOnRectangleIcon" size={24} variant="solid" />
                                <span>Sign In</span>
                            </a>
                        )
                    )}
                </div>

                {/* Trust Indicators */}
                <div className="flex flex-wrap justify-center gap-8 text-white/90 mb-16">
                    <div className="flex items-center gap-2">
                        <Icon name="CheckBadgeIcon" size={22} variant="solid" className="text-green-400" />
                        <span className="text-sm font-semibold">{t('verifiedManufacturers')}</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <Icon name="GlobeAltIcon" size={22} variant="solid" className="text-blue-400" />
                        <span className="text-sm font-semibold">120+ {t('totalCategories')}</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <Icon name="ShieldCheckIcon" size={22} variant="solid" className="text-amber-400" />
                        <span className="text-sm font-semibold">{t('isoCertified')}</span>
                    </div>
                </div>

                {/* Navigation Dots */}
                <div className="flex gap-3 justify-center mb-6">
                    {carouselSlides.map((_, index) => (
                        <button
                            key={index}
                            onClick={() => goToSlide(index)}
                            className={`transition-all duration-300 ${index === currentIndex
                                    ? 'w-12 h-3 bg-amber-500 rounded-full'
                                    : 'w-3 h-3 bg-white/40 hover:bg-white/60 rounded-full'
                                }`}
                            aria-label={`Go to slide ${index + 1}`}
                        />
                    ))}
                </div>

                {/* Scroll Indicator */}
                <div className="animate-bounce">
                    <div className="flex flex-col items-center gap-2 text-amber-200">
                        <span className="text-xs tracking-widest uppercase font-semibold">{t('exploreCategories')}</span>
                        <Icon name="ChevronDownIcon" size={24} variant="outline" className="opacity-80" />
                    </div>
                </div>
            </div>

            {/* Decorative Bottom Gradient */}
            <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-stone-900 via-stone-900/80 to-transparent z-[3]" />
        </section>
    );
};

export default HeroCarousel;
